import { z } from 'zod'
import { newId } from './ids.js'

/**
 * Structured, human-authored card intent used to prepare an implementation prompt.
 *
 * Everything here is optional and every field defaults to empty, so existing cards,
 * fixtures and legacy imports stay valid. Nothing in this module generates content:
 * Blueprint organises what the user wrote, it does not invent requirements.
 */

/** Bumped when the stored shape changes, so a later migration can recognise old cards. */
export const INTENT_VERSION = 1

export const INTENT_LIMITS = {
  userOutcome: 2_000,
  criterion: 1_000,
  criteriaCount: 20,
  inScope: 4_000,
  outOfScope: 4_000,
  constraints: 4_000,
  notes: 8_000,
  verification: 4_000,
  openQuestions: 4_000,
  requirementHeading: 200,
  requirementBody: 8_000,
  requirementCount: 30,
  referenceLabel: 200,
  referenceLocator: 2_000,
  referenceRelevance: 1_000,
  referenceExcerpt: 8_000,
  referenceCount: 10,
  /** Aggregate bound across one card's intent, guarding board memory and API bodies. */
  aggregate: 100_000,
  /** Prompt output above this asks the user to shorten rather than truncating silently. */
  exportCharacters: 50_000,
} as const

/**
 * Suggested groups a user can add. Each carries writing prompts — questions to answer,
 * not answers. Guidance is never emitted into a prompt; only what the user writes is.
 */
export interface RequirementGroupDefinition {
  key: string
  label: string
  guidance: string
}

export const REQUIREMENT_GROUPS: readonly RequirementGroupDefinition[] = [
  {
    key: 'ui_screen',
    label: 'UI / screen',
    guidance:
      'Screen purpose, entry point, visible information, primary and secondary actions, navigation destinations, existing screens to follow.',
  },
  {
    key: 'layout',
    label: 'Layout',
    guidance:
      'Content order, grouping, columns, alignment, spacing, width and height constraints, scrolling ownership, sticky areas, action placement.',
  },
  {
    key: 'forms_fields',
    label: 'Forms and fields',
    guidance:
      'Field names and labels, input types, required or optional, defaults, placeholders, help text, allowed values, dependencies and conditional visibility.',
  },
  {
    key: 'validation_submission',
    label: 'Validation and submission',
    guidance:
      'Field and cross-field rules, validation timing, exact error messages, disabled and submitting states, duplicate submission handling, success outcome, unsaved-change behaviour.',
  },
  {
    key: 'components',
    label: 'Components',
    guidance:
      'Existing component to reuse or extend, suggested name, variants, inputs and outputs, events, composition, reusable versus screen-specific behaviour.',
  },
  {
    key: 'interaction_navigation',
    label: 'Interaction and navigation',
    guidance:
      'Click and keyboard behaviour, selection, focus changes, dialogs, dismissal, drag and drop, undo, transitions, destination after completing an action.',
  },
  {
    key: 'visual_styling',
    label: 'Visual styling',
    guidance:
      'Design-system references, typography, colour roles, borders, icons, density, theme support, examples of the desired appearance.',
  },
  {
    key: 'responsive',
    label: 'Responsive behaviour',
    guidance:
      'Target devices, breakpoints where known, wrapping and stacking, overflow, touch interactions, which controls change position or visibility.',
  },
  {
    key: 'accessibility',
    label: 'Accessibility',
    guidance:
      'Semantic controls, accessible names, keyboard sequence, focus return, error announcements, contrast expectations, reduced-motion behaviour.',
  },
  {
    key: 'states_feedback',
    label: 'States and feedback',
    guidance:
      'Initial, loading, empty, partial, success, validation-error, server-error, unavailable and permission-denied states, with recovery actions for each relevant one.',
  },
  {
    key: 'content_language',
    label: 'Content and language',
    guidance:
      'Exact labels, button text, messages, tooltips, terminology, date and number formatting, translation and long-text expectations.',
  },
  {
    key: 'data_rules',
    label: 'Data and business rules',
    guidance:
      'Required data, field meanings, examples, computed values, invariants, defaults, edge cases, what must persist.',
  },
  {
    key: 'api_integration',
    label: 'API and integration',
    guidance:
      'Known endpoints, request and response examples, authentication expectations, error handling, retries, external-system assumptions.',
  },
  {
    key: 'permissions_privacy',
    label: 'Permissions and privacy',
    guidance:
      'Who may view or change what, sensitive fields, masking, prohibited logging, retention requirements where relevant.',
  },
  {
    key: 'performance_compatibility',
    label: 'Performance and compatibility',
    guidance:
      'Expected data volumes, measurable response and interaction targets, supported browsers and devices, constraints on dependencies.',
  },
  {
    key: 'existing_implementation',
    label: 'Existing implementation',
    guidance:
      'Relevant repository paths, components, conventions, current behaviour to preserve, known technical limitations.',
  },
  {
    key: 'defect_reproduction',
    label: 'Defect reproduction',
    guidance:
      'Current versus expected behaviour, reproduction steps, environment, frequency, a minimal example.',
  },
  {
    key: 'testing_examples',
    label: 'Testing and examples',
    guidance:
      'Concrete inputs and outputs, happy paths, boundary cases, regression scenarios, how to demonstrate acceptance.',
  },
  {
    key: 'delivery_rollout',
    label: 'Delivery and rollout',
    guidance:
      'Migration needs, backward compatibility, feature flags, deployment dependencies, rollout conditions — when the task requires them.',
  },
  {
    key: 'other',
    label: 'Other context',
    guidance: 'Anything the suggested groups do not cover. Give it your own heading.',
  },
] as const

const GROUP_KEYS = new Set(REQUIREMENT_GROUPS.map((group) => group.key))

export function requirementGroup(key: string): RequirementGroupDefinition | undefined {
  return REQUIREMENT_GROUPS.find((group) => group.key === key)
}

/** Unknown keys are kept rather than dropped, so a card from a newer build is not damaged. */
export function requirementGroupLabel(key: string): string {
  return requirementGroup(key)?.label ?? key
}

export function isKnownRequirementGroup(key: string): boolean {
  return GROUP_KEYS.has(key)
}

// --- schemas ---------------------------------------------------------------

const bounded = (max: number) => z.string().max(max).default('')

export const acceptanceCriterionSchema = z.object({
  id: z.string().min(1),
  text: z.string().max(INTENT_LIMITS.criterion).default(''),
})

export const requirementEntrySchema = z.object({
  id: z.string().min(1),
  /** Catalogue key, or any string for a group added by a later build. */
  group: z.string().min(1).max(64),
  heading: z.string().max(INTENT_LIMITS.requirementHeading).default(''),
  body: z.string().max(INTENT_LIMITS.requirementBody).default(''),
  /** Entries are included by default; collapsing or hiding never excludes them. */
  include: z.boolean().default(true),
})

export const contextReferenceSchema = z.object({
  id: z.string().min(1),
  label: z.string().max(INTENT_LIMITS.referenceLabel).default(''),
  /** An http(s) URL or a repository-relative path. Never fetched or read. */
  locator: z.string().max(INTENT_LIMITS.referenceLocator).default(''),
  relevance: z.string().max(INTENT_LIMITS.referenceRelevance).default(''),
  /** Pasted by the user. Only this is carried into a prompt; a locator is a pointer. */
  excerpt: z.string().max(INTENT_LIMITS.referenceExcerpt).default(''),
  include: z.boolean().default(true),
})

/** Per-field inclusion for existing card controls. Title and description are always in. */
export const planningInclusionSchema = z.object({
  type: z.boolean().default(true),
  status: z.boolean().default(true),
  priority: z.boolean().default(true),
  moscow: z.boolean().default(true),
  owner: z.boolean().default(true),
  tags: z.boolean().default(true),
  release: z.boolean().default(true),
  dueDate: z.boolean().default(true),
  estimateMinutes: z.boolean().default(true),
  /** Offered, not default: board location and colour are optional inclusions. */
  location: z.boolean().default(false),
  colour: z.boolean().default(false),
})

export const cardIntentSchema = z.object({
  version: z.number().int().min(1).default(INTENT_VERSION),
  userOutcome: bounded(INTENT_LIMITS.userOutcome),
  acceptanceCriteria: z.array(acceptanceCriterionSchema).max(INTENT_LIMITS.criteriaCount).default([]),
  inScope: bounded(INTENT_LIMITS.inScope),
  outOfScope: bounded(INTENT_LIMITS.outOfScope),
  constraints: bounded(INTENT_LIMITS.constraints),
  notes: bounded(INTENT_LIMITS.notes),
  verification: bounded(INTENT_LIMITS.verification),
  openQuestions: bounded(INTENT_LIMITS.openQuestions),
  requirements: z.array(requirementEntrySchema).max(INTENT_LIMITS.requirementCount).default([]),
  references: z.array(contextReferenceSchema).max(INTENT_LIMITS.referenceCount).default([]),
  planning: planningInclusionSchema.prefault({}),
})

export type AcceptanceCriterion = z.infer<typeof acceptanceCriterionSchema>
export type RequirementEntry = z.infer<typeof requirementEntrySchema>
export type ContextReference = z.infer<typeof contextReferenceSchema>
export type PlanningInclusion = z.infer<typeof planningInclusionSchema>
export type CardIntent = z.infer<typeof cardIntentSchema>

export function emptyCardIntent(): CardIntent {
  return cardIntentSchema.parse({})
}

export function newAcceptanceCriterion(text = ''): AcceptanceCriterion {
  return { id: newId(), text }
}

export function newRequirementEntry(group: string, heading = ''): RequirementEntry {
  return { id: newId(), group, heading, body: '', include: true }
}

export function newContextReference(): ContextReference {
  return { id: newId(), label: '', locator: '', relevance: '', excerpt: '', include: true }
}

// --- helpers ---------------------------------------------------------------

/** Total characters a user has written into intent, for the aggregate bound. */
export function intentCharacterCount(intent: CardIntent): number {
  let total =
    intent.userOutcome.length +
    intent.inScope.length +
    intent.outOfScope.length +
    intent.constraints.length +
    intent.notes.length +
    intent.verification.length +
    intent.openQuestions.length
  for (const criterion of intent.acceptanceCriteria) total += criterion.text.length
  for (const entry of intent.requirements) total += entry.heading.length + entry.body.length
  for (const reference of intent.references) {
    total += reference.label.length + reference.locator.length + reference.relevance.length + reference.excerpt.length
  }
  return total
}

export function intentIsEmpty(intent: CardIntent): boolean {
  return (
    intentCharacterCount(intent) === 0 &&
    intent.acceptanceCriteria.length === 0 &&
    intent.requirements.length === 0 &&
    intent.references.length === 0
  )
}

/** Only http(s) locators are offered as clickable links; anything else renders as text. */
export function isLinkableLocator(locator: string): boolean {
  const trimmed = locator.trim()
  if (!/^https?:\/\//i.test(trimmed)) return false
  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}
